import { FormEvent, useEffect, useState } from 'react';
import AdminLayout from '../AdminLayout';
import {
  fetchAdminAccounts,
  updateAdminAccountRole,
  deleteAdminAccount,
  createAdminAccount,
  approveAdminAccount,
  AdminAccountSummary,
} from '../adminService';
import { useAdminSession, ADMIN_ROLE_LABEL, ADMIN_STATUS_LABEL, AdminRole } from '../adminSession';
import { ApiError } from '../../../services/apiClient';
import styles from './AdminAccounts.module.scss';

function AdminAccounts() {
  const [session, setSession] = useAdminSession();
  const [admins, setAdmins] = useState<AdminAccountSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('ADMIN');
  const [creating, setCreating] = useState(false);

  const isSuperAdmin = session?.admin.role === 'SUPER_ADMIN';

  const load = () => {
    if (!session) return;
    fetchAdminAccounts(session.token)
      .then(setAdmins)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setSession(null);
        } else if (err instanceof ApiError && err.status === 403) {
          setLoadError('최종관리자만 이용할 수 있는 메뉴입니다.');
        } else {
          setLoadError(err instanceof Error ? err.message : '관리자 목록을 불러오지 못했습니다.');
        }
      });
  };

  useEffect(() => {
    if (session && isSuperAdmin) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isSuperAdmin]);

  if (session && !isSuperAdmin) {
    return (
      <AdminLayout title="관리자 계정 관리">
        <div className="section">
          <div className="card">최종관리자만 이용할 수 있는 메뉴입니다.</div>
        </div>
      </AdminLayout>
    );
  }

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!session) return;
    setActionError(null);
    setCreating(true);
    try {
      await createAdminAccount(session.token, {
        username,
        password,
        name: name || undefined,
        email: email || undefined,
        role,
      });
      setUsername('');
      setPassword('');
      setName('');
      setEmail('');
      setRole('ADMIN');
      setLoadError(null);
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '관리자 계정 생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleToggle = async (admin: AdminAccountSummary) => {
    if (!session) return;
    const nextRole: AdminRole = admin.role === 'SUPER_ADMIN' ? 'ADMIN' : 'SUPER_ADMIN';
    if (!window.confirm(`${admin.username} 계정을 ${ADMIN_ROLE_LABEL[nextRole]}(으)로 변경하시겠습니까?`)) return;

    setActionError(null);
    try {
      await updateAdminAccountRole(session.token, admin.id, nextRole);
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '권한 변경 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (admin: AdminAccountSummary) => {
    if (!session) return;
    if (!window.confirm(`${admin.username} 계정을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;

    setActionError(null);
    try {
      await deleteAdminAccount(session.token, admin.id);
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '삭제 중 오류가 발생했습니다.');
    }
  };

  const handleApprove = async (admin: AdminAccountSummary) => {
    if (!session) return;
    if (!window.confirm(`${admin.username} 계정의 가입 신청을 승인하시겠습니까?`)) return;

    setActionError(null);
    try {
      await approveAdminAccount(session.token, admin.id);
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '승인 처리 중 오류가 발생했습니다.');
    }
  };

  return (
    <AdminLayout title="관리자 계정 관리">
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">관리자 계정</div>
          <div className="sec-actions">
            <span className="chip active">전체 {admins?.length ?? 0}명</span>
          </div>
        </div>

        {loadError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {loadError}
          </div>
        )}
        {actionError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {actionError}
          </div>
        )}

        {!loadError && (
          <div className="card" style={{ padding: 0, marginBottom: 24 }}>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>아이디</th>
                    <th>이름</th>
                    <th>이메일</th>
                    <th>권한</th>
                    <th>상태</th>
                    <th>가입일</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {!admins && (
                    <tr>
                      <td colSpan={7} className="emptyRow">
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                  {admins && admins.length === 0 && (
                    <tr>
                      <td colSpan={7} className="emptyRow">
                        등록된 관리자가 없습니다.
                      </td>
                    </tr>
                  )}
                  {admins?.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.username}</td>
                      <td>{admin.name || '-'}</td>
                      <td>{admin.email || '-'}</td>
                      <td>
                        <span className={`badgePill ${admin.role === 'SUPER_ADMIN' ? 'ok' : 'info'}`}>
                          <span className="pulse"></span> {ADMIN_ROLE_LABEL[admin.role]}
                        </span>
                      </td>
                      <td>
                        <span className={`badgePill ${admin.status === 'APPROVED' ? 'ok' : 'warn'}`}>
                          <span className="pulse"></span> {ADMIN_STATUS_LABEL[admin.status]}
                        </span>
                      </td>
                      <td>{new Date(admin.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className={styles.rowActions}>
                          {admin.status === 'PENDING' && (
                            <button type="button" className="btn btn-outline" onClick={() => handleApprove(admin)}>
                              승인
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline"
                            disabled={admin.id === session?.admin.id}
                            onClick={() => handleRoleToggle(admin)}
                          >
                            {admin.role === 'SUPER_ADMIN' ? '일반관리자로 변경' : '최종관리자로 변경'}
                          </button>
                          <button
                            type="button"
                            className={`btn btn-outline ${styles.dangerBtn}`}
                            disabled={admin.id === session?.admin.id}
                            onClick={() => handleDelete(admin)}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>
            새 관리자 추가
          </div>
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.field}>
              <label htmlFor="new-admin-username">아이디</label>
              <input
                id="new-admin-username"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="new-admin-password">비밀번호</label>
              <input
                id="new-admin-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="new-admin-name">이름</label>
              <input id="new-admin-name" type="text" value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className={styles.field}>
              <label htmlFor="new-admin-email">이메일</label>
              <input
                id="new-admin-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="new-admin-role">권한</label>
              <select id="new-admin-role" value={role} onChange={(event) => setRole(event.target.value as AdminRole)}>
                {(Object.keys(ADMIN_ROLE_LABEL) as AdminRole[]).map((value) => (
                  <option key={value} value={value}>
                    {ADMIN_ROLE_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? '생성 중...' : '계정 생성'}
            </button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminAccounts;
